import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Error Boundary component to catch and handle React component errors gracefully.
 * Prevents entire page crashes when a component throws an error.
 * 
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          padding={3}
          textAlign="center"
        >
          <ErrorOutlineIcon style={{ fontSize: 60, color: '#f44336', marginBottom: 16 }} />
          <Typography variant="h5" gutterBottom style={{ fontWeight: 600 }}>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            An unexpected error occurred while rendering this component.
          </Typography>
          {this.state.error && (
            <Box
              marginTop={2}
              padding={2}
              bgcolor="rgba(244, 67, 54, 0.1)"
              borderRadius="4px"
              maxWidth="600px"
              width="100%"
            >
              <Typography variant="body2" style={{ fontFamily: 'monospace', color: '#d32f2f' }}>
                {this.state.error.message}
              </Typography>
            </Box>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleReset}
            style={{ marginTop: 24 }}
          >
            Try Again
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

